import { TicketCategory } from '../../models/chamados.models';

/**
 * A árvore de categorias.
 *
 * A categoria deixou de ser uma lista plana: um nível agrupa (Financeiro,
 * Suporte) e o nível analítico é o que carrega a configuração de verdade —
 * fila, SLA, canais e acesso. Quem abre chamado escolhe **sempre uma folha**,
 * e o grupo serve para reduzir a escolha a um punhado de opções por vez.
 *
 * As funções ficam aqui, fora dos componentes, porque as duas telas que
 * dependem delas — o cadastro e o modal de abertura — precisam da MESMA
 * definição de "é grupo", "é folha" e "qual é o caminho até aqui". Duas
 * implementações divergiriam já na primeira mudança.
 */

/** Nó da árvore, no formato que o cascader e a lista consomem. */
export interface NoDeCategoria {
    /** O documento original. */
    categoria: TicketCategory;
    /** Profundidade: 0 = raiz. */
    nivel: number;
    filhos: NoDeCategoria[];
}

/** Item do cascader: `value` é o `_id`, `isLeaf` fecha a navegação. */
export interface OpcaoCascata {
    value: string;
    label: string;
    isLeaf: boolean;
    /** Grupo sem folha permitida não deve nem aparecer — ver `podarArvore`. */
    children?: OpcaoCascata[];
    /** Desabilita o grupo como escolha final. */
    disabled?: boolean;
}

/**
 * Monta a árvore a partir da lista plana.
 *
 * `parentId` que aponta para categoria inexistente (excluída, de outra
 * empresa) vira RAIZ em vez de sumir: categoria órfã escondida é categoria que
 * ninguém consegue achar para corrigir.
 */
export function montarArvore(categorias: TicketCategory[]): NoDeCategoria[] {

    const porId = new Map<string, NoDeCategoria>();

    for (const categoria of categorias ?? []) {
        porId.set(categoria._id, { categoria: categoria, nivel: 0, filhos: [] });
    }

    const raizes: NoDeCategoria[] = [];

    for (const no of porId.values()) {

        const paiId = no.categoria.parentId;
        const pai = paiId != null ? porId.get(paiId) : null;

        if (pai == null || pai === no) {
            raizes.push(no);
            continue;
        }

        pai.filhos.push(no);
    }

    // Ciclo (A é pai de B e B é pai de A) deixaria nós fora das raízes e
    // inalcançáveis. O que sobrou sem caminho até uma raiz volta a ser raiz.
    const alcancaveis = new Set<string>();
    marcarAlcancaveis(raizes, alcancaveis);

    for (const no of porId.values()) {

        if (alcancaveis.has(no.categoria._id)) {
            continue;
        }

        // Promover a raiz NAO basta: o pai continua apontando para ele, e a
        // travessia entraria em laco infinito. Desliga do pai antes de subir.
        const pai = no.categoria.parentId != null ? porId.get(no.categoria.parentId) : null;

        if (pai != null) {
            pai.filhos = pai.filhos.filter(filho => filho !== no);
        }

        raizes.push(no);
        marcarAlcancaveis([no], alcancaveis);
    }

    ordenarEDefinirNivel(raizes, 0);
    return raizes;
}

function marcarAlcancaveis(nos: NoDeCategoria[], vistos: Set<string>): void {

    for (const no of nos) {

        if (vistos.has(no.categoria._id)) {
            continue;
        }

        vistos.add(no.categoria._id);
        marcarAlcancaveis(no.filhos, vistos);
    }
}

function ordenarEDefinirNivel(nos: NoDeCategoria[], nivel: number): void {

    nos.sort((um, outro) => (um.categoria.nome ?? '').localeCompare(outro.categoria.nome ?? ''));

    for (const no of nos) {
        no.nivel = nivel;
        ordenarEDefinirNivel(no.filhos, nivel + 1);
    }
}

/** Achata a árvore na ordem de exibição, com o nível para a indentação. */
export function achatarArvore(nos: NoDeCategoria[]): NoDeCategoria[] {

    const lista: NoDeCategoria[] = [];

    for (const no of nos) {
        lista.push(no);
        lista.push(...achatarArvore(no.filhos));
    }

    return lista;
}

/** Categoria que agrupa outras. A configuração dela é ignorada. */
export function ehGrupo(categoria: TicketCategory, categorias: TicketCategory[]): boolean {
    return (categorias ?? []).some(item => item.parentId === categoria?._id);
}

/**
 * Ids que NÃO podem ser pai da categoria informada.
 *
 * Ela mesma e toda a sua descendência: eleger um descendente como pai criaria
 * um ciclo, e o cadastro sumiria da árvore inteira.
 */
export function idsProibidosComoPai(categoria: TicketCategory, categorias: TicketCategory[]): Set<string> {

    const proibidos = new Set<string>();

    if (categoria?._id == null) {
        return proibidos;
    }

    proibidos.add(categoria._id);

    let cresceu = true;

    while (cresceu) {

        cresceu = false;

        for (const item of categorias ?? []) {
            if (item.parentId != null && proibidos.has(item.parentId) && !proibidos.has(item._id)) {
                proibidos.add(item._id);
                cresceu = true;
            }
        }
    }

    return proibidos;
}

/** "Financeiro › Boletos › Segunda via", para mostrar o caminho completo. */
export function caminhoDaCategoria(categoria: TicketCategory, categorias: TicketCategory[]): string {

    const nomes: string[] = [];
    const vistos = new Set<string>();

    let atual = categoria;

    while (atual != null && !vistos.has(atual._id)) {

        vistos.add(atual._id);
        nomes.unshift(atual.nome);

        atual = atual.parentId != null
            ? (categorias ?? []).find(item => item._id === atual.parentId)
            : null;
    }

    return nomes.join(' › ');
}

/**
 * Converte a árvore em opções de cascata, deixando só o que a pessoa pode
 * escolher.
 *
 * `folhaPermitida` decide cada folha (canal, acesso, ativa). Grupo cujas
 * folhas foram todas descartadas é PODADO: oferecer um caminho que termina em
 * lista vazia é pior que não oferecer caminho nenhum.
 */
export function montarCascata(
    nos: NoDeCategoria[],
    folhaPermitida: (categoria: TicketCategory) => boolean
): OpcaoCascata[] {

    const opcoes: OpcaoCascata[] = [];

    for (const no of nos) {

        const ehFolha = no.filhos.length === 0;

        if (ehFolha) {

            if (folhaPermitida(no.categoria)) {
                opcoes.push({ value: no.categoria._id, label: no.categoria.nome, isLeaf: true });
            }

            continue;
        }

        const filhos = montarCascata(no.filhos, folhaPermitida);

        if (filhos.length === 0) {
            continue;
        }

        opcoes.push({
            value: no.categoria._id,
            label: no.categoria.nome,
            isLeaf: false,
            // Grupo não é escolha final: o chamado sempre nasce numa folha, que
            // é quem tem fila, SLA e prazo.
            disabled: false,
            children: filhos,
        });
    }

    return opcoes;
}

/** Caminho de ids da raiz até a categoria — o valor que o cascader espera. */
export function caminhoDeIds(categoriaId: string, categorias: TicketCategory[]): string[] {

    const caminho: string[] = [];
    const vistos = new Set<string>();

    let atual = (categorias ?? []).find(item => item._id === categoriaId);

    while (atual != null && !vistos.has(atual._id)) {

        vistos.add(atual._id);
        caminho.unshift(atual._id);

        atual = atual.parentId != null
            ? (categorias ?? []).find(item => item._id === atual.parentId)
            : null;
    }

    return caminho;
}
